/**
 * Tilbudslinjer display modes (customer-facing).
 * Default `kapittel` = Offer::Group structure (falls back to calc group headers).
 * Legacy session value `per_calc` migrates to the default (one calc per offer).
 */

import {
  buildGroupedDisplayRows,
  readOfferGroupId,
  shouldShowGroupHierarchy,
} from "./offer-calc-item-groups";

export const OFFER_LINES_VIEW_MODES = Object.freeze({
  KAPITTEL: "kapittel",
  FLAT: "flat",
  KUNDEGRUPPER: "kundegrupper",
});

/** @deprecated Migrated to DEFAULT on load — kept for recognition only. */
export const LEGACY_PER_CALC_VIEW_MODE = "per_calc";

export const DEFAULT_OFFER_LINES_VIEW_MODE = OFFER_LINES_VIEW_MODES.KAPITTEL;

const STORAGE_PREFIX = "promang.offer.linesViewMode.";

function isGroupRowLocal(item) {
  const type = item?.item_type || item?.type;
  if (type === "group" || item?.group_row === true) return true;
  if (["product", "manual", "hours", "calculation", "text"].includes(type)) {
    return false;
  }
  const qty = item?.quantity;
  if (qty === null || qty === undefined || qty === "") return true;
  return false;
}

export function isValidOfferLinesViewMode(mode) {
  return Object.values(OFFER_LINES_VIEW_MODES).includes(mode);
}

export function loadOfferLinesViewMode(offerId) {
  if (offerId == null || typeof sessionStorage === "undefined") {
    return DEFAULT_OFFER_LINES_VIEW_MODE;
  }
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${offerId}`);
    if (raw === LEGACY_PER_CALC_VIEW_MODE) {
      sessionStorage.setItem(
        `${STORAGE_PREFIX}${offerId}`,
        DEFAULT_OFFER_LINES_VIEW_MODE
      );
      return DEFAULT_OFFER_LINES_VIEW_MODE;
    }
    if (isValidOfferLinesViewMode(raw)) return raw;
  } catch {
    /* ignore */
  }
  return DEFAULT_OFFER_LINES_VIEW_MODE;
}

export function saveOfferLinesViewMode(offerId, mode) {
  if (offerId == null || typeof sessionStorage === "undefined") return;
  if (!isValidOfferLinesViewMode(mode)) return;
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${offerId}`, mode);
  } catch {
    /* ignore */
  }
}

function syntheticCalcGroup(item) {
  return isGroupRowLocal(item) && readOfferGroupId(item) == null;
}

function perCalcRows(items) {
  // Do not invent Claude-style «1.0» numbers on kalkyle title chapters —
  // customer/PDF must show the real calc title (e.g. «Kalkyle»), not «Kalkyle 1.0».
  return (items || []).map((item, index) => ({
    key: String(item?.id ?? item?.draggableId ?? `line-${index}`),
    kind: isGroupRowLocal(item) ? "group" : "item",
    item,
    itemIndex: index,
    depth: 0,
    isChapterHeader: isGroupRowLocal(item),
  }));
}

/**
 * Build table rows for the customer lines tab / print preview.
 *
 * @returns {Array<{
 *   key: string,
 *   kind: 'group'|'item'|'ungrouped',
 *   item?: object,
 *   itemIndex?: number|null,
 *   group?: object,
 *   depth: number,
 *   isChapterHeader: boolean,
 * }>}
 */
export function buildOfferLinesDisplayRows({
  items = [],
  groups = [],
  viewMode = DEFAULT_OFFER_LINES_VIEW_MODE,
  collapsedGroupIds = null,
} = {}) {
  const mode = isValidOfferLinesViewMode(viewMode)
    ? viewMode
    : DEFAULT_OFFER_LINES_VIEW_MODE;

  // Fallback when no Offer::Group chapters exist — keep calc group headers.
  if (
    (mode === OFFER_LINES_VIEW_MODES.KAPITTEL ||
      mode === OFFER_LINES_VIEW_MODES.KUNDEGRUPPER) &&
    !shouldShowGroupHierarchy(groups)
  ) {
    return perCalcRows(items);
  }

  if (mode === OFFER_LINES_VIEW_MODES.FLAT) {
    return (items || [])
      .map((item, itemIndex) => ({ item, itemIndex }))
      .filter(({ item }) => !isGroupRowLocal(item))
      .map(({ item, itemIndex }) => ({
        key: String(item?.id ?? item?.draggableId ?? `line-${itemIndex}`),
        kind: "item",
        item,
        itemIndex,
        depth: 0,
        isChapterHeader: false,
      }));
  }

  // kapittel / kundegrupper — Offer::Group hierarchy; skip synthetic calc headers
  const lineItems = (items || []).filter((item) => !syntheticCalcGroup(item));
  const grouped = buildGroupedDisplayRows(
    groups,
    lineItems,
    collapsedGroupIds || new Set()
  );

  // Map itemIndex back onto original `items` array for visibility updates.
  const originalIndexByIdentity = new Map();
  (items || []).forEach((item, index) => {
    const idKey = item?.id != null ? `id:${item.id}` : null;
    const dragKey = item?.draggableId ? `d:${item.draggableId}` : null;
    if (idKey) originalIndexByIdentity.set(idKey, index);
    if (dragKey) originalIndexByIdentity.set(dragKey, index);
  });

  const resolveOriginalIndex = (item) => {
    if (!item) return null;
    if (item.id != null) {
      const idx = originalIndexByIdentity.get(`id:${item.id}`);
      if (idx != null) return idx;
    }
    if (item.draggableId) {
      const idx = originalIndexByIdentity.get(`d:${item.draggableId}`);
      if (idx != null) return idx;
    }
    return null;
  };

  let rows = grouped.map((row, i) => {
    if (row.kind === "group") {
      return {
        key: `g-${row.group?.id ?? i}`,
        kind: "group",
        group: row.group,
        // Raw title + number — chapterLabel/formatGroupLabel formats once
        // (avoid «Title 1.0 1.0» from double formatGroupLabel).
        item: {
          title: row.group?.title || row.group?.discipline || "—",
          description: row.group?.comment || "",
          number: row.group?.number,
          discipline: row.group?.discipline,
          item_type: "group",
          group_row: true,
        },
        itemIndex: null,
        depth: row.depth || 0,
        isChapterHeader: true,
      };
    }
    if (row.kind === "ungrouped") {
      return {
        key: `ungrouped-${i}`,
        kind: "ungrouped",
        item: {
          title: null,
          description: null,
          item_type: "group",
          group_row: true,
          _ungrouped: true,
        },
        itemIndex: null,
        depth: row.depth || 0,
        isChapterHeader: true,
      };
    }
    const item = row.item;
    return {
      key: String(item?.id ?? item?.draggableId ?? `line-${row.itemIndex}`),
      kind: "item",
      item,
      itemIndex: resolveOriginalIndex(item),
      depth: row.depth || 0,
      isChapterHeader: false,
    };
  });

  if (mode === OFFER_LINES_VIEW_MODES.KUNDEGRUPPER) {
    // Drop ungrouped bucket + its lines; keep only chapter members.
    const keep = new Set();
    let include = false;
    rows.forEach((row, idx) => {
      if (row.kind === "ungrouped") {
        include = false;
        return;
      }
      if (row.kind === "group") {
        include = true;
        keep.add(idx);
        return;
      }
      if (include && row.kind === "item" && readOfferGroupId(row.item) != null) {
        keep.add(idx);
      }
    });
    // Remove empty chapter headers (no following kept item before next group)
    rows = rows.filter((row, idx) => {
      if (!keep.has(idx)) return false;
      if (row.kind !== "group") return true;
      for (let j = idx + 1; j < rows.length; j += 1) {
        if (rows[j].kind === "group" || rows[j].kind === "ungrouped") break;
        if (keep.has(j) && rows[j].kind === "item") return true;
      }
      return false;
    });
  }

  return rows;
}
